import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["scanInput", "display", "binPrompt"]
  static values = { pendingItemId: Number }

  connect() {
    this.scanInputTarget.focus()
  }

  scan(event) {
    event.preventDefault()
    const input = this.scanInputTarget.value.trim()
    this.scanInputTarget.value = ''

    if (!input) return

    // Check if we're in bin assignment mode
    if (this.pendingItemIdValue && input.startsWith('HOLDINGBIN:')) {
      this.assignBin(input)
      return
    }

    fetch('/qc_station/scan', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content },
      body: JSON.stringify({ scan_input: input })
    })
    .then(r => r.json())
    .then(data => {
      if (data.success) {
        if (data.type === 'holding_bin') {
          this.displayBinContents(data.bin)
        } else {
          this.displayJob(data.job, data.scanned_item_id)
        }
      } else {
        this.showError(data.error)
      }
      this.scanInputTarget.focus()
    })
    .catch(error => {
      console.error('Error:', error)
      this.showError('An error occurred while scanning')
      this.scanInputTarget.focus()
    })
  }

  displayBinContents(bin) {
    this.displayTarget.classList.remove('hidden')
    this.pendingItemIdValue = 0

    const itemsHtml = bin.items.length > 0
      ? bin.items.map(item => this.renderBinItem(item)).join('')
      : '<tr><td colspan="7" class="px-3 py-8 text-center text-sm text-gray-500">This bin is empty</td></tr>'

    const html = `
      <div class="bg-white shadow sm:rounded-lg mb-6">
        <div class="px-4 py-5 sm:px-6 flex justify-between items-center">
          <div>
            <h3 class="text-2xl font-semibold text-gray-900">Holding Bin: ${bin.identifier}</h3>
            <p class="mt-1 text-sm text-gray-500">${bin.items_count} item(s) in bin</p>
          </div>
          ${bin.items_count > 0 ? `
            <button
              data-action="click->qc-station#clearBin"
              data-bin-id="${bin.id}"
              class="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-500">
              Clear Bin
            </button>
          ` : ''}
        </div>
      </div>

      <div class="overflow-x-auto shadow ring-1 ring-black/5 sm:rounded-lg">
        <table class="min-w-full divide-y divide-gray-300">
          <thead class="bg-gray-50">
            <tr>
              <th class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6" style="min-width: 120px;">Preview</th>
              <th class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Order</th>
              <th class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Product</th>
              <th class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Color</th>
              <th class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Size</th>
              <th class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Location</th>
              <th class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Placed At</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white">
            ${itemsHtml}
          </tbody>
        </table>
      </div>
    `

    this.displayTarget.innerHTML = html
  }

  renderBinItem(item) {
    const thumbnailHTML = item.thumbnail_url ?
      `<img src="${item.thumbnail_url}" alt="Print preview" style="width: 120px; min-width: 120px; height: auto;" class="object-contain rounded border border-gray-300 bg-gray-200">` :
      `<div style="width: 120px; min-width: 120px; height: 137px;" class="flex items-center justify-center bg-gray-100 rounded border border-gray-300 text-gray-400 text-xs">No image</div>`

    return `
      <tr class="bg-white">
        <td class="py-4 pl-4 pr-3 sm:pl-6">${thumbnailHTML}</td>
        <td class="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">${item.ordernumber}</td>
        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${item.product || '-'}</td>
        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${item.color || '-'}</td>
        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${item.size || '-'}</td>
        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${item.print_location || '-'}</td>
        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${item.placed_at || '-'}</td>
      </tr>
    `
  }

  clearBin(event) {
    const binId = event.currentTarget.dataset.binId

    if (!confirm('Are you sure you want to clear all items from this bin?')) {
      return
    }

    fetch(`/holding_bins/${binId}/clear`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content }
    })
    .then(r => r.json())
    .then(data => {
      if (data.success) {
        this.showSuccess(data.message)
        setTimeout(() => {
          this.displayTarget.classList.add('hidden')
          this.displayTarget.innerHTML = ''
          this.scanInputTarget.focus()
        }, 1500)
      } else {
        this.showError(data.error)
      }
    })
  }

  displayJob(job, scannedItemId) {
    this.displayTarget.classList.remove('hidden')
    this.pendingItemIdValue = 0

    const html = `
      <div class="bg-white shadow sm:rounded-lg mb-6">
        <div class="px-4 py-5 sm:px-6">
          <h3 class="text-2xl font-semibold text-gray-900">Order #${job.ordernumber}</h3>
          <div class="mt-2 grid grid-cols-2 gap-4">
            <div>
              <p class="text-sm text-gray-500">Production Date</p>
              <p class="text-sm font-medium text-gray-900">${job.production_date || 'Not set'}</p>
            </div>
            <div>
              <p class="text-sm text-gray-500">Job Status</p>
              <p class="text-sm font-medium text-gray-900">${job.status}</p>
            </div>
          </div>
        </div>
      </div>

      <div id="bin-prompt" data-qc-station-target="binPrompt" class="hidden mb-6"></div>

      <div class="overflow-x-auto shadow ring-1 ring-black/5 sm:rounded-lg">
        <table class="min-w-full divide-y divide-gray-300">
          <thead class="bg-gray-50">
            <tr>
              <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6" style="min-width: 200px;">Preview</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Item ID</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Product</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Color</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Size</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Location</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Bin</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">QC Action</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white">
            ${this.renderItems(job.items, scannedItemId)}
          </tbody>
        </table>
      </div>
    `

    this.displayTarget.innerHTML = html
  }

  renderItems(items, scannedItemId) {
    return items.map(item => {
      const isScanned = item.id === scannedItemId
      const isQCPass = item.status && item.status.includes('QC Pass')
      const isQCFail = item.status && item.status.includes('QC Fail')

      // Determine row background and border
      let bgClass = 'bg-white'
      let borderClass = ''

      if (isScanned && !isQCPass && !isQCFail) {
        // Scanned item that hasn't been QC'd yet
        bgClass = 'bg-indigo-50'
        borderClass = 'border-l-4 border-l-indigo-600'
      } else if (isQCPass) {
        // Already QC passed
        bgClass = 'bg-green-50'
      } else if (isQCFail) {
        // Already QC failed
        bgClass = 'bg-red-50'
      }

      const thumbnailHTML = item.thumbnail_url ?
        `<img src="${item.thumbnail_url}" alt="Print preview" style="width: 200px; min-width: 200px; height: auto;" class="object-contain rounded border border-gray-300 bg-gray-200">` :
        `<div style="width: 200px; min-width: 200px; height: 229px;" class="flex items-center justify-center bg-gray-100 rounded border border-gray-300 text-gray-400 text-sm">No image</div>`

      // Show QC buttons only for the scanned item
      const actionHTML = isScanned && !isQCPass && !isQCFail ? `
        <div class="flex space-x-2">
          <button data-item-id="${item.id}" data-action="click->qc-station#pass" class="rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-500">
            Pass
          </button>
          <button data-item-id="${item.id}" data-action="click->qc-station#fail" class="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-500">
            Fail
          </button>
        </div>
      ` : '-'

      // Bin location column
      const binHTML = item.holding_bin_identifier
        ? `<span class="inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-700/10">${item.holding_bin_identifier}</span>`
        : '-'

      return `
        <tr class="${borderClass} ${bgClass}">
          <td class="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6" style="min-width: 200px;">
            ${thumbnailHTML}
          </td>
          <td class="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">${item.order_item_id || '-'}</td>
          <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${item.product || '-'}</td>
          <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${item.color || '-'}</td>
          <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${item.size || '-'}</td>
          <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${item.print_location || '-'}</td>
          <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
            ${this.statusBadgeHTML(item.status)}
          </td>
          <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${binHTML}</td>
          <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
            ${actionHTML}
          </td>
        </tr>
      `
    }).join('')
  }

  statusBadgeHTML(status) {
    const badgeClass = this.getStatusBadgeClass(status)
    return `<span class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${badgeClass}">${status}</span>`
  }

  getStatusBadgeClass(status) {
    if (status.includes('QC Pass') || status.includes('Complete')) return 'bg-green-50 text-green-700 ring-green-600/20'
    if (status.includes('QC Fail') || status.includes('Fail')) return 'bg-red-50 text-red-700 ring-red-600/20'
    if (status.includes('QC Started')) return 'bg-blue-50 text-blue-700 ring-blue-600/20'
    if (status.includes('Pending')) return 'bg-yellow-50 text-yellow-700 ring-yellow-600/20'
    return 'bg-gray-50 text-gray-700 ring-gray-600/20'
  }

  pass(event) { this.updateQC(event.currentTarget.dataset.itemId, 'pass') }
  fail(event) { this.updateQC(event.currentTarget.dataset.itemId, 'fail') }

  updateQC(itemId, result) {
    fetch('/qc_station/update_qc', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content },
      body: JSON.stringify({ item_id: itemId, result: result })
    })
    .then(r => r.json())
    .then(data => {
      if (data.success) {
        if (data.needs_bin_placement) {
          this.showBinPrompt(itemId, data.sibling_bins || [])
        } else {
          this.showSuccess(data.message)
          // Clear display after 2 seconds
          setTimeout(() => {
            this.displayTarget.classList.add('hidden')
            this.displayTarget.innerHTML = ''
            this.scanInputTarget.focus()
          }, 2000)
        }
      } else {
        this.showError(data.error)
      }
    })
    .catch(error => {
      console.error('Error:', error)
      this.showError('An error occurred during QC update')
    })
  }

  showBinPrompt(itemId, siblingBins) {
    this.pendingItemIdValue = itemId

    const siblingInfo = siblingBins.length > 0
      ? `<div class="mt-3 text-sm text-gray-600">
           <p class="font-medium">Other items from this order are in:</p>
           <ul class="mt-1 list-disc list-inside">
             ${siblingBins.map(b => `<li><span class="font-semibold text-purple-700">${b.bin_identifier}</span> - ${b.product} ${b.color} ${b.size}</li>`).join('')}
           </ul>
         </div>`
      : ''

    const promptHtml = `
      <div class="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <svg class="h-6 w-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
            </svg>
          </div>
          <div class="ml-3 flex-1">
            <h3 class="text-lg font-medium text-amber-800">Place Item in Holding Bin</h3>
            <p class="mt-1 text-sm text-amber-700">
              Other items in this order are not QC'd yet. Scan a holding bin QR code to place this item.
            </p>
            ${siblingInfo}
            <div class="mt-3">
              <button
                data-action="click->qc-station#skipBinPlacement"
                class="text-sm text-amber-600 hover:text-amber-800 underline">
                Skip (item will not be tracked)
              </button>
            </div>
          </div>
        </div>
      </div>
    `

    if (this.hasBinPromptTarget) {
      this.binPromptTarget.innerHTML = promptHtml
      this.binPromptTarget.classList.remove('hidden')
    }

    this.scanInputTarget.focus()
  }

  assignBin(input) {
    const itemId = this.pendingItemIdValue

    fetch('/qc_station/assign_bin', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content },
      body: JSON.stringify({ item_id: itemId, bin_identifier: input })
    })
    .then(r => r.json())
    .then(data => {
      if (data.success) {
        this.showSuccess(`Item placed in ${data.bin_identifier}`)
        this.pendingItemIdValue = 0
        setTimeout(() => {
          this.displayTarget.classList.add('hidden')
          this.displayTarget.innerHTML = ''
          this.scanInputTarget.focus()
        }, 2000)
      } else {
        this.showError(data.error)
        this.scanInputTarget.focus()
      }
    })
  }

  skipBinPlacement() {
    this.pendingItemIdValue = 0
    this.showSuccess('QC Pass recorded')
    setTimeout(() => {
      this.displayTarget.classList.add('hidden')
      this.displayTarget.innerHTML = ''
      this.scanInputTarget.focus()
    }, 2000)
  }

  showSuccess(message) {
    const successDiv = document.createElement('div')
    successDiv.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg z-50 text-lg'
    successDiv.textContent = message
    document.body.appendChild(successDiv)

    setTimeout(() => {
      successDiv.remove()
    }, 3000)
  }

  showError(message) {
    const errorDiv = document.createElement('div')
    errorDiv.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-4 rounded-lg shadow-lg z-50 text-lg'
    errorDiv.textContent = message
    document.body.appendChild(errorDiv)

    setTimeout(() => {
      errorDiv.remove()
    }, 3000)
  }
}
